import React from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import ContactForm from "../components/ContactForm";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import SEO from "../components/SEO";

const ContactUs = () => {
  const contactMethods = [
    {
      icon: Phone,
      title: "Phone",
      details: "1-877-886-6927",
      subdetails: "Mon-Fri, 9am-6pm EST",
      link: "tel:+18778866927",
    },
    {
      icon: Mail,
      title: "Email",
      details: "info@trademarkclaimexperts.com",
      subdetails: "We respond within 24 hours",
      link: "mailto:info@trademarkclaimexperts.com",
    },
    {
      icon: MapPin,
      title: "Address",
      details: "535 Mission St San Francisco, CA 94105",
      subdetails: "",
      link: null,
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "Monday - Friday: 9am - 6pm EST",
      subdetails: "Saturday - Sunday: Closed",
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Trademark Claim Experts | Get Expert Assistance"
        description="Contact Trademark Claim Experts for expert help with trademark registration, copyright filing, Amazon Brand Registry, and company formation services."
      />
      {/* Banner */}
      <section className="py-12 bg-gradient-to-r from-brand-purple to-brand-pink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">Contact Us</h1>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              const content = (
                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-purple/10 to-brand-pink/10 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-brand-purple" />
                    </div>
                    <CardTitle>{method.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-900 font-medium mb-1">
                      {method.details}
                    </p>
                    <p className="text-sm text-gray-600">{method.subdetails}</p>
                  </CardContent>
                </Card>
              );

              return method.link ? (
                <a key={index} href={method.link}>
                  {content}
                </a>
              ) : (
                <div key={index}>{content}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Form */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Send Us a Message
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Fill out the form below and our team will get back to you as
                soon as possible.
              </p>
              <ContactForm />
            </div>

            {/* Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Why Contact Us?
                </h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-brand-pink/10 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-brand-purple text-sm font-bold">
                        ✓
                      </span>
                    </div>
                    <span>
                      Get expert advice on trademark registration and
                      intellectual property protection
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-brand-pink/10 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-brand-purple text-sm font-bold">
                        ✓
                      </span>
                    </div>
                    <span>
                      Discuss your specific business needs and find the right
                      service package
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-brand-pink/10 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-brand-purple text-sm font-bold">
                        ✓
                      </span>
                    </div>
                    <span>
                      Get answers to your questions about the trademark process
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-brand-pink/10 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <span className="text-brand-purple text-sm font-bold">
                        ✓
                      </span>
                    </div>
                    <span>
                      Request a quote or discuss custom trademark solutions
                    </span>
                  </li>
                </ul>
              </div>

              <Card className="bg-gradient-to-br from-brand-purple/5 to-brand-pink/5 border-brand-purple">
                <CardHeader>
                  <CardTitle>Quick Response Guarantee</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    We pride ourselves on responsive customer service. Our team
                    typically responds to all inquiries within 24 hours during
                    business days. For urgent matters, please call us directly.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Schedule a Consultation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Prefer to speak with us directly? Schedule a free
                    consultation with one of our trademark experts to discuss
                    your specific needs.
                  </p>
                  <a
                    href="tel:+18001234567"
                    className="text-brand-purple font-semibold hover:underline"
                  >
                    Call 1-877-886-6927 to schedule
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Placeholder */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-200 rounded-2xl h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Map Location</p>
              <p className="text-sm text-gray-500">
                535 Mission St San Francisco, CA 94105
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
